/**
 * @module atoms/button/Button
 * @description A simple, reusable button component with customizable styling
 *
 * **Atomic Design Category**: Atom
 * **SSR Safe**: Yes
 *
 * @example
 * ```tsx
 * <Button onClick={() => console.log('clicked')}>
 *   Click Me
 * </Button>
 *
 * <Button variant="primary" disabled={false}>
 *   Primary Button
 * </Button>
 * ```
 */

import React, { useCallback } from 'react';
import styles from 'atoms/button/Button.module.css';

/**
 * Button variant options
 */
const ButtonVariant = {
    Primary: 'primary',
    Secondary: 'secondary',
    Danger: 'danger'
} as const;

type ButtonVariantType = (typeof ButtonVariant)[keyof typeof ButtonVariant];

/**
 * Button type options
 */
const ButtonType = {
    Button: 'button',
    Submit: 'submit',
    Reset: 'reset'
} as const;

type ButtonTypeType = (typeof ButtonType)[keyof typeof ButtonType];

/**
 * Props for Button component
 */
interface ButtonProps {
    /** Button content */
    children: React.ReactNode;
    /** Visual style variant */
    variant?: ButtonVariantType;
    /** Click handler */
    onClick?: () => void;
    /** Disabled state */
    disabled?: boolean;
    /** Button type attribute */
    type?: ButtonTypeType;
}

const variantClass: Record<ButtonVariantType, string> = {
    [ButtonVariant.Primary]: styles['primary'],
    [ButtonVariant.Secondary]: styles['secondary'],
    [ButtonVariant.Danger]: styles['danger']
};

/**
 * Button component - A simple, reusable button with variants
 *
 * @param props - Component props
 * @returns Button element
 */
const Button: React.FC<ButtonProps> = ({
    children = 'Button',
    variant = ButtonVariant.Primary,
    onClick,
    disabled = false,
    type = ButtonType.Button
}) => {
    const className = [styles['button'], variantClass[variant], disabled && styles['disabled']].filter(Boolean).join(' ');

    const handleClick = useCallback(() => {
        if (!disabled && onClick) {
            onClick();
        }
    }, [disabled, onClick]);

    return (
        <button
            type={type}
            className={className}
            onClick={handleClick}
            disabled={disabled}
        >
            {children}
        </button>
    );
};

export type { ButtonProps, ButtonVariantType, ButtonTypeType };
export { Button, ButtonVariant, ButtonType };
